/**
 * Token Metadata Engine
 * 
 * Fetches coin information (name, symbol, logo, socials) from multiple sources:
 * 1. Pump.fun Frontend API
 * 2. PumpPortal Data API (Fallback)
 * 3. DexScreener API (Fallback)
 * 4. Synthetic Generator (Final Safety Net)
 */

import { tokenMetaCache } from "./tokenMetaCache";
import { deployerStore } from "./deployerStore";

export type TokenMeta = {
  name: string;
  symbol: string;
  imageUri: string | null;
  description: string | null;
  twitter: string | null;
  telegram: string | null;
  website: string | null;
  pumpMcUsd: number | null;
  liquidityUsd: number | null;
  volumeUsd24h: number | null;
  priceUsd: number | null;
  dexUrl: string | null;
  bonded: boolean;
  // Security Suite
  creator: string | null;
  holderCount: number | null;
  top10Percentage: number | null;
  isRugged: boolean;
  isMintDisabled: boolean;
  whaleCount: number | null;
};

type PumpCoin = {
  name: string;
  symbol: string;
  image_uri?: string;
  description?: string;
  twitter?: string;
  telegram?: string;
  website?: string;
  usd_market_cap?: number;
  complete: boolean;
  creator?: string;
};

type DexPair = {
  baseToken: { name: string; symbol: string };
  url: string;
  priceUsd: string;
  liquidity?: { usd: number };
  volume?: { h24: number };
  info?: {
    imageUrl?: string;
    socials?: Array<{ type: string; url: string }>;
  };
};

async function fetchPumpMeta(mint: string): Promise<PumpCoin | null> {
  try {
    // Attempt 1: V2 Frontend API (Faster for new launches)
    const res = await fetch(`/pump-frontend-v2/coins/${encodeURIComponent(mint)}`, {
      headers: { Accept: "application/json" },
    });
    
    if (res.ok) return (await res.json()) as PumpCoin;

    // Attempt 2: V1 Frontend API
    const v1Res = await fetch(`/pump-frontend/coins/${encodeURIComponent(mint)}`, {
      headers: { Accept: "application/json" },
    });
    if (v1Res.ok) return (await v1Res.json()) as PumpCoin;
    
    // Attempt 3: Fallback to PumpPortal Data API
    const portalRes = await fetch(`/pump-portal/api/data/token-info?mints=${mint}`);
    if (portalRes.ok) {
      const data = await portalRes.json();
      const info = Array.isArray(data) ? data[0] : data;
      if (info && info.mint) {
        return {
          name: info.name,
          symbol: info.symbol,
          image_uri: info.image,
          description: info.description,
          twitter: info.twitter,
          telegram: info.telegram,
          website: info.website,
          usd_market_cap: info.market_cap,
          complete: info.complete,
          creator: info.creator || info.creatorAddress
        };
      }
    }

    // Attempt 4: Fallback to our own backend which now scrapes on-chain IPFS directly!
    const backendRes = await fetch(`/api/token-meta?mints=${mint}`);
    if (backendRes.ok) {
      const data = await backendRes.json();
      const meta = data.meta && data.meta[mint];
      if (meta && meta.name) {
        return {
          name: meta.name,
          symbol: meta.symbol,
          image_uri: meta.image,
          description: meta.description,
          twitter: meta.twitter,
          telegram: meta.telegram,
          website: meta.website,
          usd_market_cap: 0,
          complete: false
        };
      }
    }
    return null;
  } catch {
    return null;
  }
}

async function fetchDexMeta(mint: string): Promise<DexPair | null> {
  try {
    const res = await fetch(`/dex-api/latest/dex/tokens/${encodeURIComponent(mint)}`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const j = (await res.json()) as { pairs?: DexPair[] };
    const pairs = j.pairs ?? [];
    if (pairs.length === 0) return null;
    return pairs.sort((a, b) => (b.liquidity?.usd ?? 0) - (a.liquidity?.usd ?? 0))[0] ?? null;
  } catch {
    return null;
  }
}

function normaliseLink(url: string | null): string | null {
  if (!url) return null;
  const s = url.trim();
  if (!s || s === "null" || s === "undefined") return null;
  return s;
}

export async function fetchTokenMeta(mint: string): Promise<TokenMeta | null> {
  let pump: PumpCoin | null = null;
  let dex: DexPair | null = null;

  try {
    [pump, dex] = await Promise.all([
      fetchPumpMeta(mint).catch(() => null),
      fetchDexMeta(mint).catch(() => null)
    ]);
  } catch {
    // Silence errors
  }

  // Record launch in reputation store if we found a creator
  if (pump?.creator) {
    deployerStore.recordLaunch(pump.creator, mint);
  }

  // If we have nothing from APIs, check the Cache first before falling back to synthetic
  if (!pump && !dex) {
    const cached = tokenMetaCache.get(mint);
    
    // If cache has real name, use it to prevent flickering
    if (cached && !cached.name.startsWith("Indexing ") && !cached.name.startsWith("Token ") && cached.name !== "?") {
       return {
         name: cached.name,
         symbol: cached.symbol,
         imageUri: cached.imageUri,
         description: "Restored from local memory.",
         twitter: null, telegram: null, website: null,
         pumpMcUsd: null, liquidityUsd: null, volumeUsd24h: null, priceUsd: null,
         dexUrl: null, bonded: false,
         creator: null, holderCount: null, top10Percentage: null, isRugged: false, isMintDisabled: false, whaleCount: null
       };
    }

    const short = mint.slice(0, 4).toUpperCase();
    return {
      name: `Indexing Asset...`,
      symbol: short,
      imageUri: null,
      description: "Asset metadata is being indexed from the blockchain.",
      twitter: null,
      telegram: null,
      website: null,
      pumpMcUsd: null,
      liquidityUsd: null,
      volumeUsd24h: null,
      priceUsd: null,
      dexUrl: null,
      bonded: false,
      creator: null, holderCount: null, top10Percentage: null, isRugged: false, isMintDisabled: false, whaleCount: null
    };
  }

  const twitter =
    pump?.twitter?.trim() ||
    dex?.info?.socials?.find((s) => s.type === "twitter")?.url?.trim() ||
    null;
  const telegram =
    pump?.telegram?.trim() ||
    dex?.info?.socials?.find((s) => s.type === "telegram")?.url?.trim() ||
    null;
  const website =
    pump?.website?.trim() ||
    dex?.info?.socials?.find((s) => s.type === "website")?.url?.trim() ||
    null;

  let rawName = pump?.name?.trim() || dex?.baseToken?.name?.trim() || "";
  let rawSymbol = pump?.symbol?.trim() || dex?.baseToken?.symbol?.trim() || "";
  
  if (rawName.toLowerCase().startsWith("token ") && rawName.toLowerCase().includes(rawSymbol.toLowerCase())) {
     rawName = rawName.slice(6).trim();
  }
  
  const finalName = rawName ? rawName : "-";

  const meta: TokenMeta = {
    name: finalName,
    symbol: rawSymbol || mint.slice(0, 4).toUpperCase(),
    imageUri: pump?.image_uri?.trim() || dex?.info?.imageUrl?.trim() || null,
    description: pump?.description?.trim() || null,
    twitter: normaliseLink(twitter),
    telegram: normaliseLink(telegram),
    website: normaliseLink(website),
    pumpMcUsd: pump?.usd_market_cap || null,
    liquidityUsd: dex?.liquidity?.usd || null,
    volumeUsd24h: dex?.volume?.h24 || null,
    priceUsd: dex?.priceUsd ? parseFloat(dex.priceUsd) : null,
    dexUrl: dex?.url || null,
    bonded: pump?.complete || !!dex,
    // Security 
    creator: pump?.creator || null,
    holderCount: null, 
    top10Percentage: null, 
    isRugged: !!pump?.complete && (dex?.liquidity?.usd || 0) < 100, // Basic rug check
    isMintDisabled: pump?.complete || false,
    whaleCount: null,
  };

  tokenMetaCache.set(mint, {
    mint,
    name: meta.name,
    symbol: meta.symbol,
    imageUri: meta.imageUri
  });

  return meta;
}
